// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { handleActions } from 'redux-actions';
import { toCamel } from 'src/helpers/caseManipulation';

import {
    GET_ADDRESS_VALIDATION_DATA_SUCCESS,
    RESET_ADDRESS_DATA,
    GET_WIC_ADDRESS_DATA_SUCCESS,
    SAVE_WIC_SELECTED_ADDRESS_DATA_SUCCESS,
    RESET_WIC_ADDRESS_DATA_SUCCESS,
} from './actions';

export interface AddressTypes {
    addressId: string;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    country: string;
    countryCode: string;
    countryName: string;
    countryNumber: string;
    extendedPostalCode: string;
    iso2CountryCode: string;
    iso2RegionCode: string;
    postalCode: string;
    region: string;
    regionCode: string;
    regionName: string;
    regionType: string;
    state: string;
    stateId: string;
    stateOrProvince: string;
    vatId: string;
    customerPO: string;
}

export interface WicAddressType {
    addressLine1: string;
    addressLine2: string;
    centreId: string;
    city: string;
    country: string;
    district: string;
    emailAddress: string;
    hoursOfOperation: string;
    locale: string;
    locationId: string;
    locationType: string;
    phoneNumber: string;
    string: string;
    zipcode: string;
    vatId: string;
    customerPO: string;
}

export const INITIAL_STATE: {
    validatedAddress: {
        addressMatches: AddressTypes[];
        autoCorrected: boolean;
        success: boolean;
    };
    wicAddresses: any;
    wicSelectedAddress: any;
} = {
    validatedAddress: {
        addressMatches: [],
        autoCorrected: false,
        success: false,
    },
    wicAddresses: {},
    wicSelectedAddress: undefined,
};

export type AddressDataStoreType = Readonly<typeof INITIAL_STATE>;

export default handleActions(
    {
        [GET_ADDRESS_VALIDATION_DATA_SUCCESS]: _getAddressValidationData,
        [RESET_ADDRESS_DATA]: resetAddressDataReducer,
        [GET_WIC_ADDRESS_DATA_SUCCESS]: _getWicAddress,
        [RESET_WIC_ADDRESS_DATA_SUCCESS]: _resetWicAddressData,
        [SAVE_WIC_SELECTED_ADDRESS_DATA_SUCCESS]: _saveWicSelectedAddress,
    },
    INITIAL_STATE
);

function _getAddressValidationData(
    state: any,
    { payload }: { payload: any }
): AddressDataStoreType {
    if (payload) {
        return {
            ...state,
            validatedAddress: toCamel(payload),
        };
    }
    return state;
}

function _getWicAddress(
    state: any,
    { payload }: { payload: any }
): AddressDataStoreType {
    if (payload) {
        const mappedWicAddresses = {};

        toCamel(payload).map((wicAddress: WicAddressType) =>
            transform(mappedWicAddresses, wicAddress)
        );
        return {
            ...state,
            wicAddresses: mappedWicAddresses,
        };
    }
    return state;
}

function _saveWicSelectedAddress(
    state: any,
    { payload }: { payload: any }
): AddressDataStoreType {
    if (payload) {
        return { ...state, wicSelectedAddress: payload };
    }
    return state;
}
function _resetWicAddressData(state: any, { payload }: { payload: any }) {
    return { ...state, wicSelectedAddress: undefined, wicAddresses: {} };
}
function resetAddressDataReducer(): AddressDataStoreType {
    return INITIAL_STATE;
}

function transform(mappedWicAddresses: any, wicAddress: WicAddressType) {
    const { locationId } = wicAddress;
    mappedWicAddresses[locationId] = wicAddress;
}
